import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { UsersService } from 'src/app/service/users.service';
import { ToastrNotificationService } from 'src/app/service/toastr-notification.service';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss']
})
export class NotificationsComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  public displayedColumns: string[] = [
    'select',
    'username',
    'companyName',
    'phone',
    'state',
    'city',
    'scanCount',
  ];
  public dataSource = new MatTableDataSource<any>();
  public originalData: any[] = [];
  public selection = new SelectionModel<any>(
    true,
    [],
    true,
    (a, b) => a?._id === b?._id
  );
  public spinner: boolean = false;
  public totalRecords: number = 0;
  public pageSize: number = 10;
  public pageIndex: number = 0;
  public states: any[] = [];
  public cities: any[] = [];
  public isStateLoading: boolean = false;
  public isCityLoading: boolean = false;
  public startDateControl = new FormControl<Date | null>(null);
  public endDateControl = new FormControl<Date | null>(null);
  public startNumberControl = new FormControl('');
  public endNumberControl = new FormControl('');
  public selectedState = new FormControl('');
  public selectedCity = new FormControl('');

  private currentFilters: any = {};

  constructor(
    private usersService: UsersService,
    private router: Router,
    private titleService: Title,
    private toastr: ToastrNotificationService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.titleService.setTitle('Notifications List');
    this.getAllState();
  };

  ngAfterViewInit(): void {
    if (this.paginator) {
      this.paginator.page.subscribe((event: PageEvent) => {
        this.onPageChange(event);
      });
    };

    setTimeout(() => {
      this.getAllUsers();
    });
  };

  getAllUsers(filters?: {
    startDate?: string,
    endDate?: string,
    state?: string,
    city?: string,
    startScanCount?: string,
    endScanCount?: string
  }) {
    this.spinner = true;
    const previousSelection = this.selection.selected.slice();
    this.currentFilters = filters || {};

    const paginatedFilters = {
      ...this.currentFilters,
      page: this.pageIndex + 1,
      pageSize: this.pageSize
    };

    this.usersService.getUsersWithFilters(paginatedFilters).subscribe({
      next: (response) => {
        this.originalData = response.users || response.data || [];

        if (response.pagination && response.pagination.totalUsers !== undefined) {
          this.totalRecords = response.pagination.totalUsers;
        } else if (response.total !== undefined) {
          this.totalRecords = response.total;
        } else if (response.totalRecords !== undefined) {
          this.totalRecords = response.totalRecords;
        } else {
          this.totalRecords = this.originalData.length;
        }

        this.dataSource.data = this.originalData;
        this.dataSource.sort = this.sort;
        this.restoreSelection(previousSelection);
        this.spinner = false;
      },
      error: (error) => {
        this.spinner = false;
        this.toastr.error(
          error.message ? error.message : 'Could not load Users',
          'Error'
        );
      }
    });
  };

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadPageData();
  };

  loadPageData() {
    this.getAllUsers(this.currentFilters);
  };

  restoreSelection(previousSelection: any[]) {
    if (previousSelection.length === 0) return;

    this.dataSource.data.forEach(currentItem => {
      const isSelected = previousSelection.some(selectedItem =>
        currentItem._id && selectedItem._id && currentItem._id === selectedItem._id
      );

      if (isSelected && !this.selection.isSelected(currentItem)) {
        this.selection.select(currentItem);
      };
    });
  };

  isAllSelected() {
    const currentPageData = this.getCurrentPageData();
    if (currentPageData.length === 0) {
      return false;
    };
    return currentPageData.every(row => this.selection.isSelected(row));
  };

  getCurrentPageData() {
    return this.dataSource.data;
  };

  toggleRow(row: any) {
    const wasSelected = this.selection.isSelected(row);
    this.selection.toggle(row);
    
    if (wasSelected) {
      this.toastr.info('1 record unselected', 'Unselected');
    } else {
      this.toastr.success('1 record selected', 'Selected');
    }
  };

  masterToggle() {
    if (this.isAllSelected()) {
      const currentPageData = this.getCurrentPageData();
      const count = currentPageData.length;
      currentPageData.forEach(row => this.selection.deselect(row));
      this.toastr.info(
        `${count} ${count === 1 ? 'record' : 'records'} unselected on this page`,
        'Unselected'
      );
    } else {
      const currentPageData = this.getCurrentPageData();
      const count = currentPageData.length;
      currentPageData.forEach(row => this.selection.select(row));
      this.toastr.success(
        `${count} ${count === 1 ? 'record' : 'records'} selected on this page`,
        'Selected'
      );
    };
  };

  selectAllRows() {
    if (this.totalRecords === 0) {
      this.toastr.warning('No records to select', 'Warning');
      return;
    };

    this.spinner = true;
    this.selection.clear();

    const pageSize = 100;
    const totalPages = Math.ceil(this.totalRecords / pageSize);
    let allUsers: any[] = [];
    let completedRequests = 0;

    for (let page = 1; page <= totalPages; page++) {
      const filters = {
        ...this.currentFilters,
        page: page,
        pageSize: pageSize
      };

      this.usersService.getUsersWithFilters(filters).subscribe({
        next: (response) => {
          const users = response.users || response.data || [];
          allUsers = allUsers.concat(users);
          completedRequests++;

          if (completedRequests === totalPages) {
            allUsers.forEach((row: any) => this.selection.select(row));
            this.spinner = false;
            this.toastr.success(
              `All ${allUsers.length} records selected across all pages`,
              'Success'
            );
          }
        },
        error: (error) => {
          completedRequests++;
          if (completedRequests === totalPages) {
            this.spinner = false;
            this.toastr.error('Some records could not be loaded', 'Error');
          }
        }
      });
    };
  };

  unselectAllRows() {
    if (!this.hasSelection()) {
      this.toastr.warning('No records are currently selected', 'No Selection');
      return;
    };
    
    const count = this.selection.selected.length;
    this.selection.clear();
    this.toastr.success(
      `All ${count} ${count === 1 ? 'record' : 'records'} unselected`,
      'Unselected'
    );
  };

  hasSelection(): boolean {
    return this.selection.hasValue() && this.selection.selected.length > 0;
  };

  hasActiveFilters(): boolean {
    const hasStartDateFilter = this.startDateControl.value;
    const hasEndDateFilter = this.endDateControl.value;
    const hasStateFilter = this.selectedState.value && this.selectedState.value.trim();
    const hasCityFilter = this.selectedCity.value && this.selectedCity.value.trim();
    const hasStartScanCount = this.startNumberControl.value && this.startNumberControl.value !== '';
    const hasEndScanCount = this.endNumberControl.value && this.endNumberControl.value !== '';

    return !!(hasStartDateFilter || hasEndDateFilter || hasStateFilter || hasCityFilter || hasStartScanCount || hasEndScanCount);
  };

  addNotifications() {
    if (!this.hasSelection()) {
      this.toastr.warning('Please select at least one user', 'No Selection');
      return;
    }
    const selectedUsers = this.selection.selected;
    const fcmTokens = selectedUsers
      .map(user => user.fcmToken)
      .filter(token => token);
    
    if (fcmTokens.length === 0) {
      this.toastr.error('Selected users do not have FCM tokens', 'No FCM Tokens');
      return;
    }
    
    this.router.navigate(['addNotification'], {
      state: { fcmTokens: fcmTokens }
    });
  };

  applyDateFilter() {
    const startDate = this.startDateControl.value;
    const endDate = this.endDateControl.value;
    const startScanCount = this.startNumberControl.value;
    const endScanCount = this.endNumberControl.value;
    const state = this.selectedState.value;
    const city = this.selectedCity.value;
    const filters: any = {};

    if (startDate) {
      const formattedDate = this.formatDate(startDate);
      filters.startDate = formattedDate;
    };

    if (endDate) {
      const formattedDate = this.formatDate(endDate);
      filters.endDate = formattedDate;
    };

    if (state && state.trim() !== '') {
      filters.state = state.trim();
    };

    if (city && city.trim() !== '') {
      filters.city = city.trim();
    };

    if (startScanCount !== null && startScanCount !== undefined && startScanCount !== '') {
      filters.startScanCount = startScanCount.toString();
    };

    if (endScanCount !== null && endScanCount !== undefined && endScanCount !== '') {
      filters.endScanCount = endScanCount.toString();
    }

    if (Object.keys(filters).length === 0) {
      this.toastr.info('Please select at least one filter', 'No Filters');
      return;
    };

    this.pageIndex = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    };

    this.getAllUsers(filters);
  };

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  clearFilters() {
    this.startDateControl.setValue(null);
    this.endDateControl.setValue(null);
    this.startNumberControl.setValue('');
    this.endNumberControl.setValue('');
    this.selectedState.setValue('');
    this.selectedCity.setValue('');
    this.cities = [];
    this.pageIndex = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    };

    this.getAllUsers();
  };

  getAllState() {
    this.isStateLoading = true;
    this.usersService.getState().subscribe({
      next: (data) => {
        this.states = data.states || [];
      },
      error: (error) => {
        this.toastr.error(
          error.message ? error.message : 'Could not load States',
          'Error'
        );
      },
      complete: () => this.isStateLoading = false
    });
  };

  getAllCity(state: string) {
    this.isCityLoading = true;
    this.cities = [];
    this.selectedCity.setValue('');

    this.usersService.getCity(state).subscribe({
      next: (data) => {
        this.cities = data.cities || [];
      },
      error: (error) => {
        this.toastr.error(
          error.message ? error.message : 'Could not load Cities',
          'Error'
        );
      },
      complete: () => this.isCityLoading = false
    });
  };

  onStateChange() {
    const state = this.selectedState.value;
    if (state) {
      this.getAllCity(state);
    } else {
      this.cities = [];
      this.selectedCity.setValue('');
    };
  };

  onCityChange() {
  };

}
